import React, { useMemo } from 'react'
import { Typography, Grid } from '@mui/material'
import TableTemplate from '../../Common/Table/TableTemplate'
import EventRegistrationAction from './EventActions/EventRegistrationAction'
import EventActions from './EventActions/EventActions'
import { setDateFormat, setTimeFormat } from '../../../utils/dateUtils'
import EventBadges from '../EventBadges'
import { fontSizes, fontWeights } from '../../Common/Typography/TypographyStyles'
import { Event } from '../../../redux/event/manageEvents/types'
import { useAppSelector } from '../../../redux/store'
import {
  selectEventsLoading,
  selectFilteredEventsIsNotArchived,
} from '../../../redux/event/manageEvents/selector'

interface Column {
  field: string
  headerName: string
  headerAlign?: 'left' | 'right' | 'center'
  bodyAlignment?: 'center' | 'right' | 'left'
  width?: string | number
  renderCell?: (value: any, item: any) => React.ReactNode
  renderFormat?: (value: any) => React.ReactNode
  sortable: boolean
}

interface EventTableProps {
  teamMemberDetails: any
}

const EventTable: React.FC<EventTableProps> = ({ teamMemberDetails }) => {
  const eventData = useAppSelector(selectFilteredEventsIsNotArchived)
  const loading = useAppSelector(selectEventsLoading)
  const error = useAppSelector(selectEventsLoading)
  const state = { loading, error }

  const columns = useMemo<Column[]>(
    () => [
      {
        field: 'id',
        headerName: 'EVENT ID',
        bodyAlignment: 'center',
        sortable: true,
      },
      {
        field: 'title',
        headerName: 'TITLE',
        bodyAlignment: 'center',
        sortable: true,
        renderCell: (param: string) => (
          <Typography
            sx={{
              maxWidth: 200,
              whiteSpace: 'normal',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              textAlign: 'left',
              display: 'block',
              margin: '0 auto',
              fontSize: fontSizes.size12,
              fontWeight: fontWeights[700],
            }}
          >
            {param}
          </Typography>
        ),
      },
      {
        field: 'startDate',
        headerName: 'START DATE',
        bodyAlignment: 'center',
        sortable: true,
        renderFormat: (value: string) => (
          <Grid container spacing={0} direction='column' alignContent='center'>
            <Grid>
              <Typography
                sx={{
                  fontSize: fontSizes.size12,
                  fontWeight: fontWeights[700],
                  lineHeight: 'ratio9to8',
                  letterSpacing: 'loose',
                }}
              >
                {setDateFormat(value)}
              </Typography>
            </Grid>
            <Grid>
              <Typography
                sx={{
                  fontSize: fontSizes.size12,
                  lineHeight: 'ratio9to8',
                  letterSpacing: 'loose',
                }}
              >
                {setTimeFormat(value)}
              </Typography>
            </Grid>
          </Grid>
        ),
      },
      {
        field: 'endDate',
        headerName: 'END DATE',
        bodyAlignment: 'center',
        sortable: true,
        renderFormat: (value: string) => (
          <Grid container spacing={0} direction='column' alignContent='center'>
            <Grid>
              <Typography
                sx={{
                  fontSize: fontSizes.size12,
                  fontWeight: fontWeights[700],
                  lineHeight: 'ratio9to8',
                  letterSpacing: 'loose',
                }}
              >
                {setDateFormat(value)}
              </Typography>
            </Grid>
            <Grid>
              <Typography
                sx={{
                  fontSize: fontSizes.size12,
                  lineHeight: 'ratio9to8',
                  letterSpacing: 'loose',
                }}
              >
                {setTimeFormat(value)}
              </Typography>
            </Grid>
          </Grid>
        ),
      },
      {
        field: 'category',
        headerName: 'CATEGORY',
        bodyAlignment: 'center',
        sortable: false,
        renderFormat: (value: string) => (
          <Grid container justifyContent='center' alignItems='center'>
            <EventBadges value={value} badgeType='categoryBadge' />
          </Grid>
        ),
      },
      {
        field: 'status',
        headerName: 'STATUS',
        bodyAlignment: 'center',
        sortable: false,
        renderCell: (status: string, row: Event) => (
          <Grid container justifyContent='center' alignItems='center'>
            <EventBadges
              value={row.isCompleted ? 'Completed' : status}
              badgeType={row.isCompleted ? 'completeBadge' : 'statusBadge'}
            />
          </Grid>
        ),
      },
      {
        field: 'registration',
        headerName: 'ENABLE REGISTRATION',
        bodyAlignment: 'center',
        sortable: false,
        width: 10,
        renderCell: (_params: any, row: Event) => <EventRegistrationAction body={row} />,
      },
      {
        field: '_id',
        headerName: 'ACTION',
        bodyAlignment: 'center',
        sortable: false,
        renderCell: (id, row) => <EventActions id={id} completed={row.isCompleted} />,
      },
    ],
    [teamMemberDetails],
  )

  return (
    <React.Fragment>
      <TableTemplate columns={columns} datas={eventData} state={state} uniqueField='id' />
    </React.Fragment>
  )
}

export default EventTable
